import {
  DialogContentText,
  SelectChangeEvent,
  MenuItem,
  Checkbox,
  Box,
  Stack,
} from '@mui/material';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMDropdownSelect } from '../MHM/MHMDropdownSelect';
import { useRecoilState, useRecoilValue } from 'recoil';
import { MHMLabel } from '../MHM/MHMLabel';
import {
  allDevicesMapSelector,
  selectableDevicesSelector,
} from '../../../models/selectors/device_selectors';
import { MHMTypography } from '../MHM/MHMTypography';
import { formatDeviceKey } from '../../../utils/deviceKey';
import { selectedDevicesKeysAtom } from '../../../models/atoms/device_atoms';

type SelectedDevice = {
  device_id: string;
  session_id?: string | null;
  client_identifier?: string | null;
};

const SELECT_ALL_VALUE = '__select_all__';

function formatDeviceLabel(device: SelectedDevice): string {
  const clientID = device.client_identifier
    ? `-${device.client_identifier}`
    : '';
  const sessionID = device.session_id ? ` (Session: ${device.session_id})` : '';
  return `${device.device_id}${clientID}${sessionID}`;
}

function DeviceChip({ selected }: { selected: string[] }) {
  const allDevices = useRecoilValue(allDevicesMapSelector);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {selected.map(deviceKey => {
        const device = allDevices.get(deviceKey);
        if (!device) {
          return null;
        }

        return (
          <MHMLabel
            key={deviceKey}
            label={formatDeviceLabel(device)}
            labelProps={{
              medium: true,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          />
        );
      })}
    </Box>
  );
}

export function DownloadCSVModalSelectDevices() {
  const { t } = useTranslation();
  const devices = useRecoilValue(selectableDevicesSelector);
  const [selectedDeviceKeys, setSelectedDeviceKeys] = useRecoilState(
    selectedDevicesKeysAtom,
  );
  const isAllSelected =
    devices.length > 0 &&
    devices.every(d =>
      selectedDeviceKeys.includes(formatDeviceKey(d.device_id, d.session_id)),
    );
  const isIndeterminate =
    selectedDeviceKeys.length > 0 && !isAllSelected && devices.length > 0;

  function handleChange({ target: { value } }: SelectChangeEvent<string[]>) {
    if (typeof value === 'string') {
      // This should never happen, it's just how MUI defined the type
      throw new Error(`Invalid value: ${value}`);
    }

    if (value.includes(SELECT_ALL_VALUE)) {
      setSelectedDeviceKeys(
        isAllSelected
          ? []
          : devices.map(d => formatDeviceKey(d.device_id, d.session_id)),
      );
      return;
    }

    setSelectedDeviceKeys(value);
  }

  return (
    <Stack>
      <DialogContentText sx={{ mb: '-8px !important' }}>
        {t('Select devices')}
      </DialogContentText>
      <MHMDropdownSelect
        label=""
        data-testid="Download CSV Select Devices Selector"
        value={selectedDeviceKeys}
        onChange={handleChange}
        renderValue={selected => <DeviceChip selected={selected} />}
        fullWidth
        sx={{ minHeight: '56px', width: '100%' }}
        MenuProps={{
          sx: { maxWidth: '80vw' },
          PaperProps: {
            sx: { maxHeight: 320, overflowY: 'auto' },
          },
          MenuListProps: { disablePadding: true },
        }}
        formControlProps={{ sx: { mt: 1, mb: 0 } }}
      >
        <MenuItem
          value={SELECT_ALL_VALUE}
          disabled={devices.length === 0}
          sx={theme => ({
            'gap': 1,
            'flex': 1,
            'position': 'sticky',
            'top': 0,
            'zIndex': theme.zIndex.modal + 1,
            'bgcolor': theme.palette.background.paper,
            'borderBottom': `1px solid ${theme.palette.divider}`,
            'boxShadow': theme.shadows[2],
            '&:hover': {
              bgcolor: theme.palette.background.paper,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundColor: theme.palette.background.paper,
              pointerEvents: 'none',
              zIndex: -1,
            },
          })}
        >
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            disableRipple
          />
          <MHMTypography sx={{ whiteSpace: 'break-spaces' }}>
            {t('Select all')}
          </MHMTypography>
        </MenuItem>
        {devices.map(device => (
          <MenuItem
            key={formatDeviceKey(device.device_id, device.session_id)}
            value={formatDeviceKey(device.device_id, device.session_id)}
            sx={{ gap: 1, flex: 1 }}
          >
            <Checkbox
              checked={selectedDeviceKeys.includes(
                formatDeviceKey(device.device_id, device.session_id),
              )}
              disableRipple
            />
            <MHMTypography sx={{ whiteSpace: 'break-spaces' }}>
              {formatDeviceLabel(device)}
            </MHMTypography>
          </MenuItem>
        ))}
      </MHMDropdownSelect>
    </Stack>
  );
}
